// TODO: Rename file to "CulturalLinguisticAndReligiousBackgrounds"
import { HorizontalBar, Bar } from "react-chartjs-2";
import createChartComponentsForDiagramConfigs from "./CaptionTableChartComponent.js";
import {
  shortenLabels,
  addPercentageToLabels,
  addCommasToLabels,
} from "Config/chartjs.global";
import Tables from "Components/Tables/LanguageAndCulture";
import GenericTable from "Components/Tables/GenericTable";
import Captions from "Captions";
import round from "Utils/round";
import sortFunc from "Utils/sortFunc";
import config from "Config";
import generateComparisonChartDataset from "Utils/generateComparisonChartDataset";

const DiagramData = {
  Ancestry: {
    hasComparison: ({ geo_aggregation }, showFor) =>
      showFor === "community" ||
      (showFor === "geo_aggregation" && geo_aggregation !== "ste"),
    comparisonQuery: "CATEGORY_ANCESTRY_COMPARISON",
    ChartComponent: Bar,
    TableComponent: Tables["Ancestry"] || GenericTable,
    queries: ["CATEGORY_ANCESTRY"],
    captionDataDerive: (_data) =>
      ({}.tap((data) => {
        let maxIndex = _data.datasets[0].data.reduce(
          (maxIndex, b, index) =>
            _data.datasets[0].data[maxIndex] > b ? maxIndex : index,
          0
        );
        data.max_amount = _data.datasets[0].data[maxIndex];
        data.max_ancestry =
          typeof _data.labels[maxIndex] === "object"
            ? _data.labels[maxIndex].join(" ")
            : _data.labels[maxIndex];
        data.total_perc = round(1)(
          data.max_amount /
            ((_data.total_population - (_data.total_population_na || 0)) / 100)
        );
        data.total_perc_state = round(1)(
          _data.rows_state
            .filter(
              ({ ancp_ancestry_multi_response: value }) =>
                value == data.max_ancestry
            )
            .reduce((sum, { number }) => sum + number, 0) /
            (_data.total_population_state / 100)
        );

        [data.second, data.third] = _data.labels.slice(1, 3);

        data.second_count = _data.datasets[0].data[1];
        data.second_perc =  round(1)(data.second_count/(_data.total_population/100));

        data.third_count = _data.datasets[0].data[2];
        data.third_perc =  round(1)(data.third_count/(_data.total_population/100));
      })),
    dataPipeline: () => ([
      { rows, fields },
      {
        rows: [{ number: total_population }],
      },
      { rows: rows_state },
      {
        rows: [{ number: total_population_state }],
      },
      {
        rows: [{ number: total_population_na }],
      },
      comparison_data = {},
    ]) => ({
      labels: rows.map(
        ({ ancp_ancestry_multi_response }) => ancp_ancestry_multi_response
      ),
      total_population,
      total_population_state,
      total_population_na,
      rows_state,
      comparison_data: generateComparisonChartDataset(
        comparison_data,
        "ancp_ancestry_multi_response"
      ),
      datasets: [
        {
          label: "Ancestries",
          data: rows.map(({ number }) => number),
          backgroundColor: config.primary_chart_color,
        },
      ],
      table: { rows, fields, name: "Ancestry" },
    }),
    chartOptions: {
      scales: {
        yAxes: [
          {
            ticks: { beginAtZero: true },
          },
        ],
      },
    },
    comparisonChartOptions: {
      tooltips: { callbacks: { label: addPercentageToLabels } },
      scales: {
        yAxes: [
          {
            ticks: {
              // Include a % sign in the ticks
              callback: (value, index, values) => `${value}%`,
            },
          },
        ],
      },
    },
    height: 400,
  },
  "LOTE Speakers": {
    ChartComponent: Bar,
    TableComponent:
      Tables["Languages other than English speakers"] || GenericTable,
    queries: ["CATEGORY_ENG_OR_NOT"],
    captionDataDerive: (_data) =>
      ({}.tap((data) => {
        data.total_not_english = _data.datasets[1].data[0];
        data.pct_not_english = round(1)(
          data.total_not_english /
            ((_data.total_population - (_data.total_population_na || 0)) / 100)
        );

        data.total_not_english_state = _data.rows_state[0];
        data.pct_not_english_state = round(1)(
          data.total_not_english_state / (_data.total_population_state / 100)
        );
      })),
    dataPipeline: () => ([
      { rows, fields },
      {
        rows: [{ number: total_population }],
      },
      { rows: rows_state },
      {
        rows: [{ number: total_population_state }],
      },
      {
        rows: [{ number: total_population_na }],
      },
    ]) => ({
      labels: [""],
      total_population,
      total_population_state,
      total_population_na,
      rows_state: rows_state
        .filter((r) => !r["english_spoken_at_home"])
        .map((r) => r["number"]),
      datasets: [
        {
          label: "English Only",
          backgroundColor: config.primary_chart_color,
          data: rows
            .filter((r) => r["english_spoken_at_home"])
            .map((r) => r["number"]),
        },
        {
          label: "LOTE",
          backgroundColor: config.secondary_chart_color,
          data: rows
            .filter((r) => !r["english_spoken_at_home"])
            .map((r) => r["number"]),
        },
      ],
      table: {
        rows,
        fields,
        name: "Languages other than English speakers",
        formatters: {
          english_spoken_at_home: (cell) =>
            cell === true ? "English Only" : "LOTE",
        },
      },
    }),
    chartOptions: {
      scales: {
        yAxes: [
          {
            ticks: { beginAtZero: true },
          },
        ],
      },
    },
  },
  "Languages Spoken at Home": {
    hasComparison: ({ geo_aggregation }, showFor) =>
      showFor === "community" ||
      (showFor === "geo_aggregation" && geo_aggregation !== "ste"),
    comparisonQuery: "CATEGORY_LANG_SPKN_AT_HOME_COMPARISON",
    ChartComponent: Bar,
    TableComponent: Tables["Languages Spoken at Home"] || GenericTable,
    queries: ["CATEGORY_LANG_SPKN"],
    captionDataDerive: (_data) =>
      ({}.tap((data) => {
        let maxIndex = _data.datasets[0].data.reduce(
          (maxIndex, b, index) =>
            _data.datasets[0].data[maxIndex] > b ? maxIndex : index,
          0
        );

        data.max_total = _data.datasets[0].data[maxIndex];
        data.max_language =
          typeof _data.labels[maxIndex] === "object"
            ? _data.labels[maxIndex].join(" ")
            : _data.labels[maxIndex];
        data.total_perc = round(1)(
          data.max_total /
            ((_data.total_population - _data.total_population_na || 0) / 100)
        );

        data.max_total_state = _data.rows_state.filter(
          ({ lanp_language_spoken_at_home: value }) =>
            value == data.max_language
        )[0]["number"];
        data.total_perc_state = round(1)(
          data.max_total_state / (_data.total_population_state / 100)
        );

        [data.second, data.third] = _data.labels.slice(1, 3);

        data.second_count = _data.datasets[0].data[1];
        data.second_perc =  round(1)(data.second_count/(_data.total_population/100));

        data.third_count = _data.datasets[0].data[2];
        data.third_perc =  round(1)(data.third_count/(_data.total_population/100));
      })),
    dataPipeline: () => ([
      { rows, fields },
      {
        rows: [{ number: total_population }],
      },
      { rows: rows_state },
      {
        rows: [{ number: total_population_state }],
      },
      {
        rows: [{ number: total_population_na }],
      },
      comparison_data = {},
    ]) => ({
      labels: rows.map(({ lanp_language_spoken_at_home }) =>
        shortenLabels(lanp_language_spoken_at_home)
      ),
      datasets: [
        {
          label: "Language spoken at home",
          data: rows.map(({ number }) => number),
          backgroundColor: config.primary_chart_color,
        },
      ],
      total_population,
      total_population_state,
      rows_state,
      total_population_na,
      comparison_data: generateComparisonChartDataset(
        comparison_data,
        "lanp_language_spoken_at_home"
      ),
      table: { rows, fields, name: "Languages Spoken at Home" },
    }),
    chartOptions: {
      scales: {
        yAxes: [
          {
            type: "logarithmic",
          },
        ],
      },
    },
    comparisonChartOptions: {
      tooltips: { callbacks: { label: addPercentageToLabels } },
      scales: {
        yAxes: [
          {
            ticks: {
              // Include a % sign in the ticks
              callback: (value, index, values) => `${value}%`,
            },
          },
        ],
      },
    },
    height: 400,
  },
  "Proficiency in English": {
    // missingFor2021: true,
    hasComparison: ({ geo_aggregation }, showFor) =>
      showFor === "community" ||
      (showFor === "geo_aggregation" && geo_aggregation !== "ste"),
    comparisonQuery: "CATEGORY_PROFICIENCY_IN_ENGLISH_COMPARISON",
    ChartComponent: Bar,
    TableComponent: Tables["Proficiency in English"] || GenericTable,
    queries: ["CATEGORY_PRFCY_ENG"],
    captionDataDerive: (_data) =>
      ({}.tap((data) => {
        data.proficient_total =
          _data.datasets[0].data[_data.datasets[0].data.length - 3] +
          _data.datasets[0].data[_data.datasets[0].data.length - 4];

        //let total = _data.datasets[0].data.reduce(
          //(total, current) => total + current,
          //0
        //);

        data.total_pct = round(1)(data.proficient_total / (_data.total_population / 100));

        data.proficient_total_state = 
        _data.rows_state[0] +
        _data.rows_state[1];
        //let total_state = _data.rows_state.reduce(
         // (sum, number) => sum + number,
         // 0
        //);
        data.total_pct_state = round(1)(
          data.proficient_total_state / (_data.total_population_state / 100)
        );
      })),
    dataPipeline: () => ([
      { rows, fields },
      {
        rows: [{ number: total_population }],
      },
      { rows: rows_state },
      {
        rows: [{ number: total_population_state }],
      },
      {
        rows: [],
      },
      comparison_data = {},
    ]) => ({
      labels: rows.map(
        ({ engp_proficiency_in_spoken_english }) =>
          engp_proficiency_in_spoken_english
      ),
      total_population,
      total_population_state,
      comparison_data: generateComparisonChartDataset(
        comparison_data,
        "engp_proficiency_in_spoken_english"
      ),
      rows_state: rows_state.map(({ number }) => number),
      datasets: [
        {
          label: "Proficiency in English",
          data: rows.map(({ number }) => number),
          backgroundColor: config.primary_chart_color,
        },
      ],
      table: { rows, fields, name: "Proficiency in English" },
    }),
    comparisonChartOptions: {
      tooltips: { callbacks: { label: addPercentageToLabels } },
      scales: {
        yAxes: [
          {
            ticks: {
              // Include a % sign in the ticks
              callback: (value, index, values) => `${value}%`,
            },
          },
        ],
      },
    },
  },
  "Religious Affiliation": {
    hasComparison: ({ geo_aggregation }, showFor) =>
      showFor === "community" ||
      (showFor === "geo_aggregation" && geo_aggregation !== "ste"),
    comparisonQuery: "CATEGORY_RELIGIOUS_AFFILIATION_COMPARISON",
    ChartComponent: HorizontalBar,
    TableComponent: Tables["Religious Affiliation"] || GenericTable,
    queries: ["CATEGORY_RELIGION"],
    captionDataDerive: (_data) =>
      ({}.tap((data) => {
        let maxIndex = _data.datasets[0].data.reduce(
          (maxIndex, b, index) =>
            _data.datasets[0].data[maxIndex] > b ? maxIndex : index,
          0
        );

        data.max_amount = _data.datasets[0].data[maxIndex];
        data.max_religion =
          typeof _data.labels[maxIndex] === "object"
            ? _data.labels[maxIndex].join(" ")
            : _data.labels[maxIndex];
        data.total_perc = round(1)(
          data.max_amount /
            ((_data.total_population - _data.total_population_na || 0) / 100)
        );

        data.max_amount_state = _data.rows_state.find(
          ({ relp_1: value }) => value == data.max_religion
        )["number"];
        data.total_perc_state = round(1)(
          data.max_amount_state / (_data.total_population_state / 100)
        );

        [data.second, data.third] = _data.labels.slice(1, 3);

        data.second_count = _data.datasets[0].data[1];
        data.second_perc =  round(1)(data.second_count/(_data.total_population/100));

        data.third_count = _data.datasets[0].data[2];
        data.third_perc =  round(1)(data.third_count/(_data.total_population/100));
      })),
    dataPipeline: () => ([
      { rows, fields },
      {
        rows: [{ number: total_population }],
      },
      { rows: rows_state },
      {
        rows: [{ number: total_population_state }],
      },
      {
        rows: [{ number: total_population_na }],
      },
      comparison_data = {},
    ]) => ({
      labels: rows.map(({ relp_1 }) => shortenLabels(relp_1)),
      datasets: [
        {
          label: "Religious Affiliation",
          data: rows.map(({ number }) => number),
          backgroundColor: config.primary_chart_color,
        },
      ],
      total_population,
      total_population_state,
      rows_state,
      total_population_na,
      comparison_data: generateComparisonChartDataset(
        comparison_data,
        "relp_1"
      ),
      table: { rows, fields, name: "Religious Affiliation" },
    }),
    chartOptions: {
      tooltips: { callbacks: { label: addCommasToLabels } },
    },
    comparisonChartOptions: {
      tooltips: { callbacks: { label: addPercentageToLabels } },
      scales: {
        xAxes: [
          {
            ticks: {
              maxRotation: 90,
              minRotation: 90,
              // Include a % sign in the ticks
            },
          },
        ],
        yAxes: [
          {
            ticks: {
              // Include a % sign in the ticks
              callback: (value, index, values) => `${value}%`,
            },
          },
        ],
      },
    },
  },
}.tap((diagrams) =>
  Object.keys(
    Captions["Cultural, Linguistic and Religious Backgrounds"]
  ).forEach(
    (key) =>
      (diagrams[key].captions =
        Captions["Cultural, Linguistic and Religious Backgrounds"][key])
  )
);

export default createChartComponentsForDiagramConfigs(DiagramData);



// WEBPACK FOOTER //
// ./components/Charts/LanguageAndCulture.js